/**
 * 모션 정책 — OS 의 '애니메이션 효과 끄기'(prefers-reduced-motion)를 따를지 한 곳에서 정한다.
 *
 * `<html data-motion="force">` (app/layout.tsx) 이면 OS 설정과 무관하게 모션을 켠다.
 * globals.css의 motion-safe/motion-reduce 및 일반 CSS도 같은 속성을 따른다.
 * 직접 matchMedia로 분기하지 않고 이 함수를 사용한다. 속성을 지우면 OS 설정을 다시 따른다.
 *
 * 주의: 이 설정은 전정기관 장애 등으로 모션을 끈 사용자에게도 모션을 보여 준다. 제품 결정으로 켠 것이다.
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  if (document.documentElement.dataset.motion === 'force') return false
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

/**
 * 스크롤 애니메이션 — 브라우저의 behavior: 'smooth' 는 Windows '애니메이션 효과'를 끄면 멈추고 data-motion 으로 켤 수 없다.
 * 모션이 꼭 보여야 하는 자리는 이 함수로 직접 움직인다. 움직이는 동안 스크롤 스냅을 꺼 두고 멈출 함수를 돌려준다.
 */
export function animateScroll(
  el: HTMLElement,
  top: number,
  duration = 380
): () => void {
  if (prefersReducedMotion()) {
    el.scrollTop = top
    return () => {}
  }
  const from = el.scrollTop
  const start = performance.now()
  const snap = el.style.scrollSnapType
  el.style.scrollSnapType = 'none'
  let frame = 0
  const tick = (now: number) => {
    const progress = Math.min(1, (now - start) / duration)
    el.scrollTop = from + (top - from) * (1 - (1 - progress) ** 3)
    if (progress < 1) frame = requestAnimationFrame(tick)
    else el.style.scrollSnapType = snap
  }
  frame = requestAnimationFrame(tick)
  return () => {
    cancelAnimationFrame(frame)
    el.style.scrollSnapType = snap
  }
}

/** 가장 가까운 스크롤 칸만 움직여 요소를 가운데로 옮긴다. scrollIntoView 와 달리 바깥 틀(overflow: hidden)까지 밀어 올리지 않는다 */
export function scrollToCenter(el: HTMLElement): () => void {
  let scroller = el.parentElement
  while (
    scroller &&
    !(
      /(auto|scroll)/.test(getComputedStyle(scroller).overflowY) &&
      scroller.scrollHeight > scroller.clientHeight
    )
  )
    scroller = scroller.parentElement
  if (!scroller) return () => {}
  const box = scroller.getBoundingClientRect()
  const rect = el.getBoundingClientRect()
  const top =
    scroller.scrollTop + rect.top - box.top - (box.height - rect.height) / 2
  return animateScroll(
    scroller,
    Math.max(0, Math.min(top, scroller.scrollHeight - scroller.clientHeight))
  )
}
